import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { Button } from '@/components/ui'
import { SelecaoProdutoPropostaFactory } from '@/features/simulador/brasil/domain/factories/selecao-produto-proposta-factory'
import { ConfirmacaoSelecaoValidador } from '@/features/simulador/brasil/domain/validators/confirmacao-selecao-validador'
import type { TGetSimulacoesRelacionadas } from '@/features/simulador/brasil/types/simulacao.type'
import { useTypedLocation } from '@/hooks/use-typed-location'
import { cn } from '@/infra'
import { showToast } from '@/infra/sonner'
import { PageLayout } from '@/layouts/page'
import CardSelecaoSimulacao from '@/pages/proposta-listar-produtos/CardSelecaoSimilacao'

export type TSimulacoesRelacionadasState = {
  nomeEmpreendimento: string
  simulacoes: TGetSimulacoesRelacionadas
}

export default function PropostaListarProdutos() {
  const { t } = useTranslation('proposta')
  const navigate = useNavigate()

  const { state } = useTypedLocation<TSimulacoesRelacionadasState>()
  const { relacionadas: simulacoesExtras, simulacaoPrincipal } = state.simulacoes
  const [selectedProducts, setSelectedProducts] = useState<TGetSimulacoesRelacionadas['relacionadas']>([])

  function exibirErroSeSimulacaoInvalida({
    simulacaoSelecionada,
    simulacoes,
  }: {
    simulacoes: TGetSimulacoesRelacionadas['relacionadas']
    simulacaoSelecionada: TGetSimulacoesRelacionadas['relacionadas'][number]
  }): boolean {
    const error = SelecaoProdutoPropostaFactory.create({
      tipoProdutoSimulacaoDoSimulador: simulacaoPrincipal.tipoDoProduto,
      simulacaoSelecionada,
      simulacoes,
    }).validate()

    if (error) {
      showToast({ message: error, variant: 'error' })
      return true
    }
    return false
  }

  const toggleProduct = (id: string) => {
    const simulacaoSelecionada = simulacoesExtras.find((p) => p.id === id)
    if (!simulacaoSelecionada) return

    const deveValidarDeselecao = selectedProducts.some((p) => p.id === id)

    if (deveValidarDeselecao) {
      const updatedSelection = selectedProducts.filter((p) => p.id !== id)
      if (exibirErroSeSimulacaoInvalida({ simulacaoSelecionada, simulacoes: updatedSelection })) return
      setSelectedProducts(updatedSelection)
      return
    }

    if (exibirErroSeSimulacaoInvalida({ simulacaoSelecionada, simulacoes: selectedProducts })) return
    setSelectedProducts((prev) => [...prev, simulacaoSelecionada])
  }

  const handleConfirm = () => {
    const error = new ConfirmacaoSelecaoValidador({
      simulacaoPrincipal,
      simulacoesSelecionadas: selectedProducts,
    }).validate()

    if (error) {
      showToast({ message: error, variant: 'error' })
      return
    }
    navigate('/proposta', {
      state: {
        simulacoesIds: [simulacaoPrincipal.id, ...selectedProducts.map((p) => p.id)],
      },
      replace: true,
    })
  }

  const breadCrumbDinamico = [
    { label: t('selecao_produto.breadcrumb.espelho') },
    { label: state.nomeEmpreendimento },
    { label: t('selecao_produto.breadcrumb.simulacoes') },
    { label: t('selecao_produto.breadcrumb.lancar_proposta') },
  ]

  return (
    <PageLayout disableBreadcrumb breadcrumb={breadCrumbDinamico}>
      <div className="mx-auto flex-col pb-24 sm:max-w-4xl md:pb-0">
        <h1 className="p-6 text-xl font-medium text-neutral-700">{t('selecao_produto.titulo')}</h1>
        <p className="mt-1 mb-6 hidden text-neutral-500 md:block">
          {simulacaoPrincipal.tipoDoProduto === 'Acessorio'
            ? t('selecao_produto.subtitulo_acessorio')
            : t('selecao_produto.subtitulo_unidade')}
        </p>
        <div className="bg-foreground p-6">
          <div className="mb-4 space-y-4">
            {simulacoesExtras.map((simulacao) => (
              <CardSelecaoSimulacao
                key={simulacao.id}
                simulacao={simulacao}
                selected={selectedProducts.some((p) => p.id === simulacao.id)}
                onToggleSelect={toggleProduct}
              />
            ))}
          </div>
          <div
            className={cn(
              'w-full bg-white pb-4',
              'fixed right-0 bottom-0 left-0 md:static md:border-t-0 md:bg-transparent',
            )}
          >
            <div className={cn('bt-slate-400 flex justify-end border-t pt-4 pr-6 pl-6', 'md:bg-foreground')}>
              <Button variant="default" onClick={handleConfirm} className="h-12 w-full md:h-auto md:w-auto">
                {t('selecao_produto.continuar')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
